import { Klydo } from "../Types";
import { baseClient } from "./baseRequest";

export const KLYDO_EVENT_PRIMARY_TYPE = "Klydo Event";

export enum CalendarEventsType {
  NATIONAL_HOLIDAY = "National holiday",
  LOCAL_HOLIDAY = "Local holiday",
  OTHER = "Other",
}

export enum KlydoPickMethod {
  RandomOne = "randomOne",
  All = "all",
}

export type State = {
  name: string;
  abbrev: string;
  iso: string;
};

export type GeographicRegion = {
  name: string;
  code: string;
};

export type DateTime = {
  year: number;
  month: number;
  day: number;
};

export type CalendarEvent = {
  id: string;
  date: DateTime;
  country?: GeographicRegion;
  states?: State[];
  type: string[];
  primaryType: string;
  name: string;
  description: string;
  klydoIds?: string[];
  pickMethod?: KlydoPickMethod;
  overrideOtherEvents?: boolean;
  ignoreEvent?: boolean;
};

export type PopulatedCalendarEvent = CalendarEvent & {
  klydos: Klydo[];
};

export type GetCalendarEventInput = {
  country?: string;
  year?: number;
  month?: number;
  day?: number;
  state?: string;
  klydoId?: string;
  populated?: boolean;
};

export type CalendarEventsSearchInput = {
  query: string;
  skip: number;
  limit: number;
};

export type CalendarEventApiClient = {
  getCalendarEvents: ({
    country,
    year,
    month,
    day,
    state,
    klydoId,
    populated,
  }: GetCalendarEventInput) => Promise<CalendarEvent[]>;
  upsertCalendarEvent: (
    eventId: string,
    event: Partial<CalendarEvent>,
  ) => Promise<void>;
  deleteCalendarEvent: (eventId: string) => Promise<void>;
  calendarEventsSearch: ({
    query,
    skip,
    limit,
  }: CalendarEventsSearchInput) => Promise<CalendarEvent[]>;
  getCalendarEvent: (eventId: string) => Promise<CalendarEvent | null>;
};

const calendarEventApiClient: CalendarEventApiClient = {
  getCalendarEvents: async ({
    country,
    year,
    month,
    day,
    state,
    klydoId,
    populated,
  }: GetCalendarEventInput) => {
    const params = new URLSearchParams();

    if (year) {
      params.append("year", year.toString());
    }
    if (month) {
      params.append("month", month.toString());
    }
    if (day) {
      params.append("day", day.toString());
    }
    if (country) {
      params.append("country", country);
    }
    if (state) {
      params.append("state", state);
    }
    if (klydoId !== undefined) {
      params.append("klydoId", klydoId);
    }
    if (populated !== undefined) {
      params.append("populated", populated.toString());
    }

    const url = `/admin/calendar-events?${params.toString()}`;

    return baseClient.get<CalendarEvent[]>({
      url,
    });
  },
  upsertCalendarEvent: async (eventId, event) => {
    return baseClient.put<void>({
      url: `/admin/calendar-events/${encodeURIComponent(eventId)}`,
      body: event,
    });
  },
  deleteCalendarEvent: async (eventId) => {
    return baseClient.delete<void>({
      url: `/admin/calendar-events/${encodeURIComponent(eventId)}`,
    });
  },
  calendarEventsSearch: async ({ query, skip, limit }) => {
    return baseClient.post<CalendarEvent[]>({
      url: `/admin/calendar-events/.search?query=${query}`,
      body: {
        skip,
        limit,
      },
    });
  },
  getCalendarEvent: async (eventId) => {
    return baseClient.get<CalendarEvent | null>({
      url: `/admin/calendar-events/${encodeURIComponent(eventId)}`,
    });
  },
};

export default calendarEventApiClient;
